import { useEffect, useState } from 'react'
import { useAllPayouts, useApprovePayout, useRecheckPayout, useRejectPayout } from 'src/hooks/useAdminAffiliates'
import { AdminPayout, PayoutStatus } from 'src/types/Affiliate'
import { formatCents, formatDay } from 'src/utils/affiliateFormat'
import { notify } from 'src/utils/notify'
import { PAYOUT_STATUS_OPTIONS, PayoutStatusBadge, errorMessage } from '../affiliate/labels'
import {
    Badge,
    Card,
    CardTitle,
    DangerButton,
    EmptyRow,
    ErrorText,
    InlineSpinner,
    Label,
    Muted,
    Pager,
    PrimaryButton,
    Row,
    SecondaryButton,
    Select,
    Table,
    TableWrap,
} from '../affiliate/ui'
import { Actions, InlineLink, Mono, SmallButtonStyle, useClampPage } from './shared'

export const TransfersCell = ({ payout }: { payout: AdminPayout }) => {
    const errors = payout.transfers.filter((transfer) => transfer.error)
    return (
        <div>
            <span>{payout.transfers.length}</span>
            {errors.length > 0 && (
                <Badge $tone="red" style={{ marginLeft: 6 }}>
                    {errors.length} failed
                </Badge>
            )}
            {errors.map((transfer, index) => (
                <Muted key={index} style={{ fontSize: 12, color: '#ff8a94', overflowWrap: 'anywhere' }}>
                    {formatCents(transfer.amountCents)}: {transfer.error}
                </Muted>
            ))}
        </div>
    )
}

const PayoutsTab = () => {
    const [status, setStatus] = useState<PayoutStatus | ''>('')
    const [page, setPage] = useState(1)
    const { data, isLoading, error, isPlaceholderData } = useAllPayouts(status, page)
    useClampPage(page, data?.totalPages, setPage, isPlaceholderData)
    const approve = useApprovePayout()
    const reject = useRejectPayout()
    const recheck = useRecheckPayout()

    useEffect(() => setPage(1), [status])

    const isBusy = (id: string) =>
        (approve.isPending && approve.variables === id) ||
        (reject.isPending && reject.variables?.id === id) ||
        (recheck.isPending && recheck.variables === id)

    const onApprove = (payout: AdminPayout) => {
        if (!window.confirm(`Approve and send ${formatCents(payout.amountCents)}?`)) return
        approve.mutate(payout.id, {
            onSuccess: () => notify('Payout approved.', 'success'),
            onError: (err) => notify(errorMessage(err), 'error'),
        })
    }

    const onReject = (payout: AdminPayout) => {
        const reason = window.prompt('Reason for rejecting this payout:')
        if (reason === null) return
        if (!reason.trim()) {
            notify('A reason is required.', 'error')
            return
        }
        reject.mutate(
            { id: payout.id, reason: reason.trim().slice(0, 500) },
            {
                onSuccess: () => notify('Payout rejected.', 'success'),
                onError: (err) => notify(errorMessage(err), 'error'),
            }
        )
    }

    const onRecheck = (payout: AdminPayout) =>
        recheck.mutate(payout.id, {
            onSuccess: (result) => notify(`Payout is now ${result.status.replace('_', ' ')}.`, 'info'),
            onError: (err) => notify(errorMessage(err), 'error'),
        })

    return (
        <Card>
            <Row>
                <CardTitle>Payouts</CardTitle>
            </Row>
            <Label style={{ maxWidth: 280 }}>
                Status
                <Select value={status} onChange={(e) => setStatus(e.target.value as PayoutStatus | '')}>
                    <option value="">All statuses</option>
                    {PAYOUT_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </Select>
            </Label>
            {isLoading ? (
                <InlineSpinner />
            ) : error ? (
                <ErrorText>{errorMessage(error)}</ErrorText>
            ) : (
                <>
                    <TableWrap>
                        <Table>
                            <thead>
                                <tr>
                                    <th>Affiliate</th>
                                    <th className="num">Amount</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th>Transfers</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {data && data.results.length > 0 ? (
                                    data.results.map((payout) => (
                                        <tr key={payout.id}>
                                            <td>
                                                <InlineLink to={`/admin/affiliates/${payout.affiliateId}`}>
                                                    {payout.affiliate?.code || 'View'}
                                                </InlineLink>
                                                {payout.finalPayout && (
                                                    <Badge $tone="neutral" style={{ marginLeft: 6 }}>
                                                        Final
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="num">{formatCents(payout.amountCents)}</td>
                                            <td>
                                                <PayoutStatusBadge status={payout.status} />
                                                {payout.failureReason && (
                                                    <Muted style={{ fontSize: 12, overflowWrap: 'anywhere' }}>
                                                        {payout.failureReason}
                                                    </Muted>
                                                )}
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                {formatDay(payout.createdAt)}
                                                <div>
                                                    <Mono>{payout.id}</Mono>
                                                </div>
                                            </td>
                                            <td>
                                                <TransfersCell payout={payout} />
                                            </td>
                                            <td>
                                                <Actions>
                                                    {payout.status === 'requested' && (
                                                        <>
                                                            <PrimaryButton
                                                                style={SmallButtonStyle}
                                                                disabled={isBusy(payout.id)}
                                                                onClick={() => onApprove(payout)}
                                                            >
                                                                Approve
                                                            </PrimaryButton>
                                                            <DangerButton
                                                                style={SmallButtonStyle}
                                                                disabled={isBusy(payout.id)}
                                                                onClick={() => onReject(payout)}
                                                            >
                                                                Reject
                                                            </DangerButton>
                                                        </>
                                                    )}
                                                    {payout.status === 'processing' && (
                                                        <SecondaryButton
                                                            style={SmallButtonStyle}
                                                            disabled={isBusy(payout.id)}
                                                            onClick={() => onRecheck(payout)}
                                                        >
                                                            Recheck
                                                        </SecondaryButton>
                                                    )}
                                                </Actions>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <EmptyRow colSpan={6}>No payouts.</EmptyRow>
                                )}
                            </tbody>
                        </Table>
                    </TableWrap>
                    {data && <Pager page={data.page} totalPages={data.totalPages} onChange={setPage} />}
                </>
            )}
        </Card>
    )
}

export default PayoutsTab
